import { useEffect, useMemo, useState } from "react";
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { useLogout, useMe } from "@/api/auth";
import { useBalance } from "@/api/balance";
import { LangSwitcher } from "@/components/LangSwitcher";
import { useT } from "@/lib/i18n";
import {
  ADMIN_RIGHT,
  OWNER_RIGHT,
  userHasRight,
} from "@/lib/rights";
import { useThemeMode } from "@/store/theme";

/**
 * Fresh shell - single design surface. Collapsible left sidebar
 * (icon-only at <264px or by toggle), breadcrumb top bar, generous
 * padding, indigo accent. The classic and modern shells were
 * removed; this is the only layout the app ships now.
 */

interface NavItem {
  to: string;
  label: string;
  icon: JSX.Element;
  ownerOnly?: boolean;
  /** Per-item admin gate. Use when the item lives in a non-admin
   *  group (so the whole group stays visible) but the specific link
   *  should only show for admins. */
  adminOnly?: boolean;
  /** Per-item right gate (owner bypasses, same as the server). Use
   *  for employee sections like the Bybit console (`bybit`). */
  right?: string;
}

interface NavGroup {
  label: string;
  items: NavItem[];
  adminOnly?: boolean;
}

// Customer-facing labels carry i18n keys (`nav.*`); admin items stay
// in English because the admin panel is operator-only and it's the
// only language the operator works in.
const NAV: NavGroup[] = [
  {
    label: "nav.shop",
    items: [
      { to: "/", label: "nav.home", icon: <Icon name="home" /> },
      { to: "/explore", label: "nav.explore", icon: <Icon name="grid" /> },
      { to: "/steam-gifts", label: "nav.steam_gifts", icon: <Icon name="gift" /> },
      { to: "/steam-topup", label: "nav.steam_topup", icon: <Icon name="zap" /> },
      { to: "/direct-topup", label: "nav.direct_topup", icon: <Icon name="package" /> },
    ],
  },
  {
    label: "nav.account",
    items: [
      { to: "/orders", label: "nav.orders", icon: <Icon name="bag" /> },
      { to: "/transactions", label: "nav.transactions", icon: <Icon name="list" /> },
      { to: "/balance", label: "nav.balance", icon: <Icon name="wallet" /> },
      { to: "/profile", label: "nav.profile", icon: <Icon name="user" /> },
      // Client-area section — open to every signed-in user.
      { to: "/marketplaces", label: "nav.marketplaces", icon: <Icon name="bag" /> },
    ],
  },
  {
    label: "nav.admin",
    adminOnly: true,
    items: [
      { to: "/admin", label: "Dashboard", icon: <Icon name="layout" /> },
      { to: "/admin/users", label: "Users", icon: <Icon name="users" /> },
      { to: "/admin/users-analytics", label: "Win-back", icon: <Icon name="users" /> },
      { to: "/admin/orders", label: "Orders", icon: <Icon name="bag" /> },
      { to: "/admin/transactions", label: "Transactions", icon: <Icon name="list" /> },
      { to: "/admin/topups", label: "Topups", icon: <Icon name="arrow-down" /> },
      { to: "/admin/services", label: "Services", icon: <Icon name="package" /> },
      { to: "/admin/pricing", label: "Rate pricing", icon: <Icon name="cog" /> },
      { to: "/admin/catalog", label: "Catalog", icon: <Icon name="grid" /> },
      { to: "/admin/stock", label: "Auto-stock", icon: <Icon name="layers" /> },
      { to: "/admin/visoria", label: "Visoria console", icon: <Icon name="cog" /> },
      { to: "/admin/steam-parser", label: "Steam parser", icon: <Icon name="zap" /> },
      { to: "/admin/steam-accounts", label: "Steam accounts", icon: <Icon name="gift" /> },
      // Operator's own TRON wallets: rental/staking income, investments,
      // ledger reconciliation. Owner only — the server gates the API the same way.
      { to: "/admin/tron", label: "TRON", icon: <Icon name="layers" />, ownerOnly: true },
      // BuySellVouchers seller cabinet: stock, code upload, journal. Owner only.
      { to: "/admin/bsv", label: "BuySellVouchers", icon: <Icon name="gift" />, ownerOnly: true },
      // Car telemetry log (ingest from the head-unit app, export, archive).
      { to: "/admin/carlog", label: "Car log", icon: <Icon name="list" /> },
      { to: "/admin/codes", label: "Codes", icon: <Icon name="package" /> },
      { to: "/admin/marketplace-logs", label: "Logs", icon: <Icon name="list" /> },
      { to: "/admin/bans", label: "Bans", icon: <Icon name="user" /> },
      // Money console. Route is /bybit (outside the gated admin
      // chunk) but the LINK lives here — it's an operator tool. The
      // `right` gate lets a bybit-right employee see just this item
      // of the admin group.
      { to: "/bybit", label: "Bybit", icon: <Icon name="wallet" />, right: "bybit" },
      { to: "/admin/reports", label: "Reports", icon: <Icon name="list" />, ownerOnly: true },
      { to: "/admin/audit", label: "Audit log", icon: <Icon name="list" />, ownerOnly: true },
      { to: "/admin/settings", label: "Settings", icon: <Icon name="cog" /> },
    ],
  },
];

const TITLE_KEYS: Record<string, string> = {
  "/": "nav.home",
  "/explore": "nav.explore",
  "/steam-gifts": "nav.steam_gifts",
  "/steam-topup": "nav.steam_topup",
  "/orders": "nav.orders",
  "/transactions": "nav.transactions",
  "/balance": "nav.balance",
  "/profile": "nav.profile",
  "/marketplaces": "nav.marketplaces",
};

const ADMIN_TITLES: Record<string, string> = {
  "/admin": "Admin · Dashboard",
  "/admin/users": "Admin · Users",
  "/admin/orders": "Admin · Orders",
  "/admin/transactions": "Admin · Transactions",
  "/admin/topups": "Admin · Topups",
  "/admin/services": "Admin · Services",
  "/admin/pricing": "Admin · Rate pricing",
  "/admin/catalog": "Admin · Catalog",
  "/admin/stock": "Admin · Auto-stock",
  "/admin/visoria": "Admin · Visoria console",
  "/admin/steam-accounts": "Admin · Steam accounts",
  "/admin/tron": "Admin · TRON",
  "/admin/bsv": "Admin · BuySellVouchers",
  "/admin/carlog": "Admin · Car log",
  "/admin/codes": "Admin · Codes",
  "/admin/bans": "Admin · Bans",
  "/admin/reports": "Admin · Reports",
  "/admin/audit": "Admin · Audit log",
  "/admin/settings": "Admin · Settings",
};

function isAdminPath(path: string): boolean {
  return path.startsWith("/admin");
}

export function LayoutFresh() {
  const navigate = useNavigate();
  const location = useLocation();
  const me = useMe();
  const balance = useBalance();
  const logout = useLogout();
  const isAdmin = userHasRight(me.data?.rights, ADMIN_RIGHT);
  const isOwner = userHasRight(me.data?.rights, OWNER_RIGHT);
  const themeMode = useThemeMode((s) => s.mode);
  const toggleTheme = useThemeMode((s) => s.toggle);
  const t = useT();

  // Labels in NAV are either i18n keys (`nav.*`) or raw English
  // strings (admin items). Resolve only the keyed ones, leave the
  // rest as-is so admin labels stay English.
  const tr = (label: string): string =>
    label.startsWith("nav.") ? t(label) : label;

  const pageTitle = (path: string): string => {
    if (path === "/bybit") return "Bybit";
    if (path in TITLE_KEYS) return t(TITLE_KEYS[path]);
    if (path in ADMIN_TITLES) return ADMIN_TITLES[path];
    if (isAdminPath(path)) return ADMIN_TITLES[path] || "Admin";
    if (path.startsWith("/orders/")) return t("order.title");
    if (path.startsWith("/categories/")) return t("catalog.title");
    if (path.startsWith("/menus/")) return t("nav.explore");
    return "-";
  };

  const [collapsed, setCollapsed] = useState<boolean>(() =>
    typeof window !== "undefined" &&
    window.localStorage.getItem("ns-fresh-collapsed") === "1",
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  useEffect(() => {
    setDrawerOpen(false);
    setAccountOpen(false);
  }, [location.pathname]);

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      window.localStorage.setItem("ns-fresh-collapsed", next ? "1" : "0");
    } catch { /* ignore */ }
  };

  const onLogout = async () => {
    await logout.mutateAsync().catch(() => undefined);
    navigate("/login", { replace: true });
  };

  const rights = me.data?.rights;
  const groups = useMemo(
    () =>
      NAV.map((g) => ({
        ...g,
        items: g.items.filter(
          (it) =>
            (!it.ownerOnly || isOwner)
            && (!it.adminOnly || isAdmin)
            && (!it.right || userHasRight(rights, it.right))
            // Items of an adminOnly group need admin — unless the
            // item carries its own `right` and the user holds it
            // (e.g. the Bybit console for a bybit-right employee).
            && (!g.adminOnly
              || isAdmin
              || (it.right != null && userHasRight(rights, it.right))),
        ),
      })).filter((g) => g.items.length > 0),
    [isAdmin, isOwner, rights],
  );

  return (
    <div className={`fresh-shell ${collapsed ? "collapsed" : ""}`}>
      <aside className={`fresh-sidebar ${drawerOpen ? "open" : ""}`}>
        <Link to="/" className="fresh-brand">
          <span className="fresh-brand-logo">N</span>
          <span className="fresh-brand-text">NS Cabinet</span>
        </Link>

        {groups.map((group) => (
          <div key={group.label} className="fresh-nav-section">
            <div className="fresh-nav-section-label">{tr(group.label)}</div>
            {group.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/" || item.to === "/admin"}
                title={collapsed ? tr(item.label) : undefined}
                className={({ isActive }) =>
                  `fresh-nav-link ${isActive ? "is-active" : ""}`
                }
              >
                <span className="fresh-nav-icon">{item.icon}</span>
                <span className="fresh-nav-link-label">{tr(item.label)}</span>
              </NavLink>
            ))}
          </div>
        ))}

        <div className="fresh-nav-section">
          <NavLink
            to="/api-playground"
            title={collapsed ? t("nav.api_playground") : undefined}
            className={({ isActive }) =>
              `fresh-nav-link ${isActive ? "is-active" : ""}`
            }
          >
            <span className="fresh-nav-icon"><Icon name="zap" /></span>
            <span className="fresh-nav-link-label">{t("nav.api_playground")}</span>
          </NavLink>
          {/* /api-docs is served by FastAPI (static HTML), not by React
              Router - must be a real anchor with full-page nav, not a
              <Link>. Opens in a new tab so the cabinet state stays put. */}
          <a
            href="/api-docs"
            target="_blank"
            rel="noopener noreferrer"
            title={collapsed ? "API documentation" : undefined}
            className="fresh-nav-link"
          >
            <span className="fresh-nav-icon"><Icon name="book" /></span>
            <span className="fresh-nav-link-label">API documentation</span>
          </a>
        </div>

        <div className="fresh-sidebar-foot">
          <button
            type="button"
            onClick={toggleCollapse}
            className="fresh-sidebar-foot-btn"
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <span className="fresh-nav-icon">
              <Icon name={collapsed ? "chevron-right" : "chevron-left"} />
            </span>
            <span className="fresh-sidebar-foot-text">
              {collapsed ? "Expand" : "Collapse"}
            </span>
          </button>
        </div>
      </aside>

      {drawerOpen && (
        <div
          className="fresh-drawer-backdrop"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      <div className="fresh-content">
        <header className="fresh-topbar">
          <div className="fresh-topbar-inner">
          <button
            type="button"
            className="fresh-icon-btn fresh-mobile-only"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
          >
            <Icon name="menu" />
          </button>
          <div className="fresh-crumbs">
            <span>NS Cabinet</span>
            <span>›</span>
            <span className="fresh-crumb-current">
              {pageTitle(location.pathname)}
            </span>
          </div>

          {(balance.data || me.data) && (
            <Link to="/balance" className="fresh-balance-pill">
              <span>{t("nav.balance")}</span>
              <span>${formatBalance(balance.data?.balance, me.data?.balance)}</span>
            </Link>
          )}

          <LangSwitcher />

          <button
            type="button"
            className="fresh-icon-btn"
            onClick={toggleTheme}
            title={
              themeMode === "dark"
                ? "Switch to light theme"
                : "Switch to dark theme"
            }
            aria-label="Toggle theme"
          >
            <Icon name={themeMode === "dark" ? "sun" : "moon"} />
          </button>

          <div style={{ position: "relative" }}>
            <button
              type="button"
              className="fresh-avatar"
              onClick={() => setAccountOpen((v) => !v)}
              aria-label="Account menu"
            >
              {(me.data?.username || me.data?.login || "?")[0]?.toUpperCase()}
            </button>
            {accountOpen && (
              <div className="fresh-account-menu">
                <div className="fresh-account-head">
                  <div className="fresh-account-name">
                    {me.data?.username || me.data?.login}
                  </div>
                  <div className="fresh-account-sub">
                    user · #{me.data?.id}
                  </div>
                </div>
                <Link to="/profile" className="fresh-account-item">{t("nav.profile")}</Link>
                <Link to="/balance" className="fresh-account-item">{t("nav.balance")}</Link>
                <hr style={{ margin: "6px 4px", border: 0, borderTop: "1px solid var(--color-surface-border)" }} />
                <button
                  type="button"
                  onClick={onLogout}
                  className="fresh-account-item"
                  style={{ color: "#dc2626" }}
                >
                  {t("nav.logout")}
                </button>
              </div>
            )}
          </div>
          </div>
        </header>

        <main className="fresh-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function formatBalance(
  liveStr: string | undefined,
  meValue: number | undefined,
): string {
  if (liveStr !== undefined) {
    const n = Number(liveStr);
    if (Number.isFinite(n)) return n.toFixed(2);
    return liveStr;
  }
  if (meValue !== undefined) return meValue.toFixed(2);
  return "-";
}

/* -------------------- inline SVG icons -------------------- */

function Icon({ name }: { name: string }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  switch (name) {
    case "home":
      return (
        <svg {...common}><path d="M3 12 12 3l9 9" /><path d="M5 10v10h14V10" /></svg>
      );
    case "grid":
      return (
        <svg {...common}><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>
      );
    case "gift":
      return (
        <svg {...common}><polyline points="20 12 20 22 4 22 4 12" /><rect x="2" y="7" width="20" height="5" /><line x1="12" y1="22" x2="12" y2="7" /><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" /><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" /></svg>
      );
    case "zap":
      return <svg {...common}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>;
    case "bag":
      return <svg {...common}><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6L18 2H6z" /><line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 0 1-8 0" /></svg>;
    case "list":
      return <svg {...common}><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>;
    case "wallet":
      return <svg {...common}><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" /><path d="M3 5v14a2 2 0 0 0 2 2h16v-5" /><path d="M18 12a2 2 0 0 0 0 4h4v-4Z" /></svg>;
    case "user":
      return <svg {...common}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>;
    case "users":
      return <svg {...common}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>;
    case "layout":
      return <svg {...common}><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" /></svg>;
    case "package":
      return <svg {...common}><line x1="16.5" y1="9.4" x2="7.5" y2="4.21" /><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></svg>;
    case "layers":
      return <svg {...common}><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></svg>;
    case "cog":
      return <svg {...common}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>;
    case "arrow-down":
      return <svg {...common}><line x1="12" y1="5" x2="12" y2="19" /><polyline points="19 12 12 19 5 12" /></svg>;
    case "menu":
      return <svg {...common}><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>;
    case "palette":
      return <svg {...common}><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="3" /><circle cx="6.5" cy="9.5" r="1" /><circle cx="9.5" cy="6" r="1" /><circle cx="14.5" cy="6.5" r="1" /></svg>;
    case "chevron-left":
      return <svg {...common}><polyline points="15 18 9 12 15 6" /></svg>;
    case "chevron-right":
      return <svg {...common}><polyline points="9 18 15 12 9 6" /></svg>;
    case "sun":
      return <svg {...common}><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" /></svg>;
    case "moon":
      return <svg {...common}><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></svg>;
    case "book":
      return <svg {...common}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></svg>;
    default:
      return <svg {...common}><circle cx="12" cy="12" r="9" /></svg>;
  }
}
